import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  PiSparkleLight, PiCheckCircleLight, PiWarningLight, PiCircleDashedLight,
  PiArrowsClockwiseLight, PiCaretDownLight,
} from 'react-icons/pi';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { getDraftIntelligence, DraftIntelligence } from '../../utils/draftIntelligenceApi';
import { rerunDraftFromSource } from '../../utils/draftSourceApi';
import { rerunReviewFromPlaybook } from '../../utils/reviewSourceApi';
import { markAsContract, getContractByDocument } from '../../utils/contractsApi';
import { PiScrollLight } from 'react-icons/pi';
import { useHistory } from 'react-router-dom';

/**
 * Document Intelligence - the at-a-glance "is this document still aligned with its
 * sources" flag, plus an expandable insights panel with one-click re-runs.
 *
 * The banner is the loud call-to-action for a single case; this is the persistent,
 * comprehensive status (template + branding, and playbook once drafts link to one)
 * that a legal professional can scan and act on. Foundation for the CLM document
 * intelligence view and, later, the list column + multi-run.
 */
interface Props {
  draftUuid: string;
  /** Re-check after an external change (e.g. a rerun completing). */
  refreshKey?: number | string;
  /** So a queued re-run can join the live progress group (same as the banner). */
  onRerunQueued?: (pubsub?: { group: string; correlation_id: string }) => void;
}

const KIND_LABEL: Record<string, string> = {
  template: 'Structure template',
  branding: 'Branding',
  playbook: 'Playbook',
};

const DocumentIntelligence: React.FC<Props> = ({ draftUuid, refreshKey, onRerunQueued }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const [intel, setIntel] = useState<DraftIntelligence | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [trackedContractUuid, setTrackedContractUuid] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!draftUuid) return;
    try {
      setIntel(await getDraftIntelligence(draftUuid));
    } catch {
      setIntel(null); // never break the page over a status read
    }
  }, [draftUuid]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Is this draft already tracked as a contract? Drives the footer state so we
  // don't re-prompt "Track as contract" on something already tracked.
  useEffect(() => {
    if (!draftUuid) return;
    let alive = true;
    getContractByDocument('draft', draftUuid)
      .then((r) => { if (alive) setTrackedContractUuid(r.tracked && r.contract ? r.contract.uuid : null); })
      .catch(() => { /* non-blocking */ });
    return () => { alive = false; };
  }, [draftUuid, refreshKey]);

  const runTemplateRefresh = useCallback(async () => {
    setBusy(true);
    try {
      const res = await rerunDraftFromSource(draftUuid);
      if (res.status === 'queued') {
        dispatch(showFeedbackModal({
          showModal: true, modalType: 'success', duration: 4000,
          message: t('Refreshing this draft from the updated template…'),
        }));
        onRerunQueued?.(res.pubsub);
        setOpen(false);
      } else if (res.status === 'no_baseline') {
        dispatch(showFeedbackModal({
          showModal: true, modalType: 'warning', duration: 6000,
          message: res.reason || t('This draft predates change-tracking — regenerate it to apply the current template.'),
        }));
      }
      await load();
    } catch {
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'error', duration: 4000,
        message: t('Could not refresh from the template. Please try again.'),
      }));
    } finally {
      setBusy(false);
    }
  }, [draftUuid, dispatch, t, onRerunQueued, load]);

  const runPlaybookRerun = useCallback(async (sessionUuid?: string) => {
    if (!sessionUuid) return;
    setBusy(true);
    try {
      const res = await rerunReviewFromPlaybook(sessionUuid);
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: res.status === 'processing' ? 'success' : 'info',
        duration: 4000,
        message: res.status === 'processing'
          ? t('Re-reviewing the changed clauses against the latest playbook…')
          : t('This review is already up to date with its playbook.'),
      }));
      await load();
    } catch {
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'error', duration: 4000,
        message: t('Could not re-run from the playbook. Please try again.'),
      }));
    } finally {
      setBusy(false);
    }
  }, [dispatch, t, load]);

  const [trackingContract, setTrackingContract] = useState(false);
  const trackAsContract = useCallback(async () => {
    setTrackingContract(true);
    try {
      const res = await markAsContract('draft', draftUuid);
      setTrackedContractUuid(res.uuid);
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'success', duration: 4000,
        message: t('Tracking as a contract — extracting its lifecycle facts. See it under Contracts.'),
      }));
    } catch {
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'error', duration: 4000,
        message: t('Could not track this as a contract. Please try again.'),
      }));
    } finally {
      setTrackingContract(false);
    }
  }, [draftUuid, dispatch, t]);

  if (!intel || intel.status === 'unlinked' || intel.status === 'unknown') return null;

  const pill = {
    aligned: { color: 'green' as const, icon: <PiCheckCircleLight className="h-3.5 w-3.5" />, label: t('Up to date') },
    behind: { color: 'yellow' as const, icon: <PiWarningLight className="h-3.5 w-3.5" />, label: t('Update available') },
    not_generated: { color: 'gray' as const, icon: <PiCircleDashedLight className="h-3.5 w-3.5" />, label: t('Not generated') },
  }[intel.status as 'aligned' | 'behind' | 'not_generated'] || null;

  if (!pill) return null;

  return (
    <div className="relative inline-block text-start">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-surface-sunken/70 px-2.5 py-1 text-xs font-medium text-fg-body hover:bg-surface-sunken transition-colors"
        aria-expanded={open}
      >
        <PiSparkleLight className="h-3.5 w-3.5 text-champagne" />
        <Badge color={pill.color} className="text-[10px] inline-flex items-center gap-1">
          {pill.icon}{pill.label}
        </Badge>
        <PiCaretDownLight className={`h-3 w-3 text-fg-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute end-0 z-50 mt-2 w-80 rounded-xl glass shadow-xl p-3">
          <div className="flex items-center gap-2 px-1 pb-2">
            <PiSparkleLight className="h-4 w-4 text-champagne" />
            <span className="text-sm font-semibold text-fg-strong">{t('Document intelligence')}</span>
          </div>
          <p className="px-1 pb-2 text-xs text-fg-muted">{intel.headline}</p>

          <div className="space-y-2">
            {intel.signals.map((s, i) => {
              const behind = s.status === 'behind';
              return (
                <div key={i} className="rounded-lg border border-hairline-soft bg-surface-sunken/70 p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-fg-body">{t(KIND_LABEL[s.kind] || s.kind)}</span>
                    <Badge color={behind ? 'yellow' : s.status === 'aligned' ? 'green' : 'gray'} className="text-[10px]">
                      {behind ? t('Behind') : s.status === 'aligned' ? t('Aligned') : t(s.status)}
                    </Badge>
                  </div>
                  {behind && (s.from_version != null || s.to_version != null) && (
                    <p className="mt-1 text-[11px] text-fg-muted">
                      {t('v{{from}} → v{{to}}', { from: s.from_version ?? '—', to: s.to_version ?? '—' })}
                    </p>
                  )}
                  {behind && s.action === 'rerun_from_source' && (
                    <Button
                      variant="primary"
                      size="small"
                      className="mt-2"
                      onClick={runTemplateRefresh}
                      isLoading={busy}
                      icon={<PiArrowsClockwiseLight className="h-3.5 w-3.5" />}
                    >
                      {t('Refresh from template')}
                    </Button>
                  )}
                  {behind && s.action === 'reapply_branding' && (
                    <p className="mt-1 text-[11px] text-fg-muted">
                      {t('Branding is applied on download — re-download to get the updated branding.')}
                    </p>
                  )}
                  {behind && s.action === 'rerun_review' && (
                    <Button
                      variant="primary"
                      size="small"
                      className="mt-2"
                      onClick={() => runPlaybookRerun(s.review_session_uuid)}
                      isLoading={busy}
                      icon={<PiArrowsClockwiseLight className="h-3.5 w-3.5" />}
                    >
                      {t('Re-run changed clauses')}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-3 border-t border-hairline-soft pt-2.5">
            {trackedContractUuid ? (
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 px-1 text-xs text-fg-muted">
                  <PiScrollLight className="h-3.5 w-3.5 text-champagne" />
                  {t('Tracked as a contract')}
                </div>
                <Button
                  variant="neutral"
                  size="small"
                  className="w-full !justify-center"
                  onClick={() => history.push(`/contracts/${trackedContractUuid}`)}
                  icon={<PiScrollLight className="h-3.5 w-3.5" />}
                >
                  {t('View contract')}
                </Button>
              </div>
            ) : (
              <Button
                variant="neutral"
                size="small"
                className="w-full !justify-center"
                onClick={trackAsContract}
                isLoading={trackingContract}
                icon={<PiScrollLight className="h-3.5 w-3.5" />}
              >
                {t('Track as contract')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentIntelligence;
